import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  FunctionComponent,
} from 'react';
import * as api from '../api';
import { getRealmFromHostname } from '../api';
import { SplashScreen } from '../dls';
import { PRODUCT_NAME } from '../branding/constants';
import { AppError, ERROR_CODE } from '../util/AppError';
import { UserNotFoundPage } from '../pages/UserNotFoundPage';
import { canAccessPairing, canAccessPatients, hasRecognizedAppRole } from '../auth/permissions';

export interface AuthenticationState {
  isAuthenticated: boolean;
  username?: string;
  organizationName?: string;
  organizationLanguage?: string;
  userRole?: string;
}

export interface AuthenticationContextValue extends AuthenticationState {
  canAccessPatients: boolean;
  canAddPatients: boolean;
  /** Redirect the browser to the Keycloak login page. */
  login: (realm?: string, loginHint?: string, prompt?: 'login') => void;
  /**
   * Redirect to login immediately (e.g. after session expiry) — skips the Keycloak logout flow.
   * Returns false when no realm can be resolved, so no redirect was started.
   */
  redirectToLogin: () => boolean;
  logout: () => void;
  refresh: () => Promise<void>;
}

const LOGIN_HINT_STORAGE_KEY_PREFIX = 'qs_login_hint';
// Scope the remembered login hint per realm so a hint from one tenant is never
// reused for another in the same browser.
const loginHintKey = (realm?: string | null): string =>
  realm ? `${LOGIN_HINT_STORAGE_KEY_PREFIX}:${realm}` : LOGIN_HINT_STORAGE_KEY_PREFIX;

const AuthenticationContext = createContext<AuthenticationContextValue | undefined>(undefined);

export function useAuthentication(): AuthenticationContextValue {
  const context = useContext(AuthenticationContext);
  if (context === undefined) {
    throw new Error('useAuthentication must be used within an AuthenticationProvider');
  }
  return context;
}

type Props = {
  children?: React.ReactNode;
};

export const AuthenticationProvider: FunctionComponent<Props> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [userNotFound, setUserNotFound] = useState(false);
  const [state, setState] = useState<AuthenticationState>({
    isAuthenticated: false,
  });
  const loginHintRef = useRef<string | undefined>(
    localStorage.getItem(loginHintKey(getRealmFromHostname())) ?? undefined,
  );

  const resolveMissingSession = useCallback((): void => {
    if (window.location.pathname === '/tenant-not-found') {
      setIsLoading(false);
      return;
    }
    const realm = getRealmFromHostname();
    if (realm) {
      // Normal missing-session recovery: no prompt, so Keycloak/Entra can do
      // silent SSO or prefill the remembered account via login_hint.
      api.login(realm, loginHintRef.current);
    } else {
      setState({ isAuthenticated: false });
      setIsLoading(false);
    }
  }, []);

  const restoreAuthenticationState = useCallback(
    async (options: { redirectOnMissingSession: boolean }): Promise<void> => {
      try {
        const session = await api.continueSession();
        if (!hasRecognizedAppRole(session.userRole)) {
          const realm = getRealmFromHostname();
          try {
            await api.localLogout();
          } finally {
            if (realm) {
              // Force the Keycloak login screen so the user can switch accounts
              // instead of being left in a locally logged-out app shell.
              api.login(realm, loginHintRef.current, 'login');
            } else {
              setState({ isAuthenticated: false });
              setIsLoading(false);
            }
          }
          return;
        }
        if (session.loginHint) {
          loginHintRef.current = session.loginHint;
          localStorage.setItem(loginHintKey(getRealmFromHostname()), session.loginHint);
        }
        setState({
          isAuthenticated: true,
          ...session,
        });
        setUserNotFound(false);
        setIsLoading(false);
      } catch (err) {
        if (err instanceof AppError && err.code === ERROR_CODE.ERR_PORTAL_USER_NOT_FOUND) {
          setUserNotFound(true);
          setIsLoading(false);
          return;
        }
        if (options.redirectOnMissingSession) {
          resolveMissingSession();
        } else {
          setState({ isAuthenticated: false });
          setIsLoading(false);
        }
      }
    },
    [resolveMissingSession],
  );

  useEffect(() => {
    const initializeAuthenticationState = async (): Promise<void> => {
      await restoreAuthenticationState({ redirectOnMissingSession: true });
    };

    initializeAuthenticationState();
  }, [restoreAuthenticationState]);

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent): void => {
      if (!event.persisted) return;
      void restoreAuthenticationState({ redirectOnMissingSession: true });
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, [restoreAuthenticationState]);

  const login = useCallback((realm?: string, loginHint?: string, prompt?: 'login'): void => {
    api.login(realm, loginHint, prompt);
  }, []);

  const redirectToLogin = useCallback((): boolean => {
    const realm = getRealmFromHostname();
    if (!realm) {
      return false;
    }
    // Session expired: recover silently (login_hint, no prompt) so an active
    // IdP session is reused instead of forcing an interactive login.
    api.login(realm, loginHintRef.current);
    return true;
  }, []);

  const logout = useCallback(() => {
    loginHintRef.current = undefined;
    localStorage.removeItem(loginHintKey(getRealmFromHostname()));
    api.logout();
  }, []);

  const refresh = useCallback(async () => {
    await api.refresh();
  }, []);

  const canAccessPatientsValue = useMemo(() => canAccessPatients(state.userRole), [state.userRole]);
  const canAddPatients = useMemo(() => canAccessPairing(state.userRole), [state.userRole]);

  const providerValue = useMemo<AuthenticationContextValue>(
    () => ({
      ...state,
      canAccessPatients: canAccessPatientsValue,
      canAddPatients,
      login,
      redirectToLogin,
      logout,
      refresh,
    }),
    [state, canAccessPatientsValue, canAddPatients, login, redirectToLogin, logout, refresh],
  );

  return (
    <AuthenticationContext.Provider value={providerValue}>
      {isLoading ? (
        <SplashScreen title={PRODUCT_NAME} loading />
      ) : userNotFound ? (
        <UserNotFoundPage onLogout={api.logout} />
      ) : (
        children
      )}
    </AuthenticationContext.Provider>
  );
};
